/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslationTupleOrId } from '@gh/core-mls';
import { ActionFlow, getRouteInitData, getRouteParentTitle, NumberTypes } from '@gh/common-shared';
import { ActivatedRoute } from '@angular/router';
import {
  PbDetails,
  PbDetailsGetItemsComponentsRequest,
  PbDetailsSave,
} from '../../resources/price-book.model';
import {
  createFilteredDataSource,
  emptySelectionModel,
  FilteredDataSource,
  mapSlicedDataItems,
  SelectionModel,
} from '@gh/core-data';
import {
  buildNumberColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  RowPointer,
} from '@gh/core-ui-grid';
import { PriceBookDetailsBusinessService } from '../../resources/price-book-details-business.service';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  getPbItemComponentId,
  PbBulkUpdateICMarkup,
} from '../../resources/pb-bulk-update-markup.model';
import { PbBulkUpdateICMarkupFormState, } from './pb-bulk-update-ic-markup-form.service';
import {
  LIGHTGREEN_MARKER_COLOR,
  RED_MARKER_COLOR,
  renderColoredArrow,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { omit } from 'lodash';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { NotificationService, WaitingService } from '@gh/core-ui';
import { DirtyType } from '@gh/common-shared-data';
import { PbCcElementDto, PbCostCenterDto } from '@gh/builder-shared-data';
import { sortByStringField } from '@gh/core-util';

export type ItemsComponentsTable = PbCcElementDto & {
  newPrice?: any;
  trendPrice?: any;
};
export type ControlsValueDto = {
  itemMarkup: number;
  compMarkup: number;
  deletePct: number;
};

@Component({
  standalone: false,
  selector: 'gh-price-book-bulk-update-ic',
  templateUrl: 'price-book-bulk-update-ic.component.html',
  styleUrls: ['price-book-bulk-update-ic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ PbBulkUpdateICMarkupFormState ],
})
export class PriceBookBulkUpdateICComponent extends AbstractFormComponent<PbBulkUpdateICMarkup> implements OnInit {
  loadedData: ItemsComponentsTable[];
  backupData: PbCcElementDto[];
  priceBook: PbDetails;
  canBeSelectedCount: number;
  gridDefinition: LayoutBasedGridDefinition;
  finderDataSource: FilteredDataSource<RowPointer, string>;
  targetSelectionModel = emptySelectionModel();
  isRowDisabledBound = this.isRowDisabled.bind(this);

  private gridApi: GridApi | undefined | null;

  get isProcessEnabled(): boolean {
    if (!this.form) {
      return false;
    }
    const { itemMarkup, deletePct, compMarkup } = this.getControlsValue();
    const hasChangesInControls = !!(itemMarkup || deletePct || compMarkup);
    if (this.targetSelectionModel.isExcludeMode) {
      return hasChangesInControls;
    }
    return hasChangesInControls && !!this.targetSelectionModel.idList.length;
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_price_book';
  }

  get isGridSelectable(): boolean {
    if (!this.form) {
      return false;
    }
    const { itemMarkup, deletePct, compMarkup } = this.getControlsValue();
    return this.canBeSelectedCount > 0 && (!!itemMarkup || !!deletePct || !!compMarkup);
  }

  constructor(
    private notificationService: NotificationService,
    public form: PbBulkUpdateICMarkupFormState,
    private route: ActivatedRoute,
    private waitingService: WaitingService,
    private changeDetectorRef: ChangeDetectorRef,
    private gridDefinitionFactory: GridDefinitionFactory,
    private businessService: PriceBookDetailsBusinessService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    ) {
    super();
    this.loadedData = [];
    this.canBeSelectedCount = 0;
  }
  ngOnInit(): void {
    const { priceBookItemsComponentsGridLayout, priceBook } = getRouteInitData(this.route);
    this.priceBook = priceBook;
    this.setGridDefinition();
    this.gridDefinition.bind(priceBookItemsComponentsGridLayout);
    this.finderDataSource = mapSlicedDataItems(
      createFilteredDataSource(
        (filter: string) => this.businessService.findRelatedItemComponentsAsDataSource(filter, sortByStringField(this.loadedData, 'code'))),
      (item) => {
        const row = this.gridApi?.getRowNode(getPbItemComponentId(item));
        return {rn: row?.rowIndex || 0, name: item.code };
      });

    this.form.build({items: [], itemMarkup: 0, compMarkup: 0, deletePct: 0, costCenter: 'All' });
    this.form.getControl('itemMarkup').valueChanges.subscribe(() => this.recalculate());
    this.form.getControl('compMarkup').valueChanges.subscribe(() => this.recalculate());
    this.form.getControl('deletePct').valueChanges.subscribe(() => this.recalculate());
    this.form.getControl('costCenter').valueChanges.subscribe(() => this.loadData());
    this.loadData();
  }

  setGridDefinition(): void {
    this.gridDefinition = this.gridDefinitionFactory
      .buildLayoutBased(
        {
          uomCode: {},
          cost: buildNumberColumn(NumberTypes.Price),
          markupPct: {
            ...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: ItemsComponentsTable }) => {
                if (data.isComponent) {
                  return this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('compMarkup').value > 0;
                }
                return this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('itemMarkup').value > 0;
              },
              'success-color': ({ data }: { data: ItemsComponentsTable }) => {
                if (data.isComponent) {
                  return this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('compMarkup').value < 0;
                }
                return this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('itemMarkup').value < 0;
              },
            },
          },
          deletePct: {...buildNumberColumn(NumberTypes.Percents_2),
            cellClassRules: {
              'warn-color': ({ data }: { data: ItemsComponentsTable }) => this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('deletePct').value > 0,
              'success-color': ({ data }: { data: ItemsComponentsTable }) => this.isSelected(data) && !this.isRowDisabled(data) && this.form.getControl('deletePct').value < 0,
            },
          },
          newPrice: {...buildNumberColumn(NumberTypes.Price),
            cellClassRules: {
              'warn-color': ({ data }: { data: ItemsComponentsTable }) => this.isSelected(data) && !this.isRowDisabled(data) &&  data.newPrice > data.price,
              'success-color': ({ data }: { data: ItemsComponentsTable }) => this.isSelected(data) && !this.isRowDisabled(data) &&  data.newPrice < data.price,
            },
          },
          price: buildNumberColumn(NumberTypes.Price),

          trendPrice: {
            cellRenderer: ({ data }: { data: ItemsComponentsTable }): string => {
              const controlValue = data.trendPrice;
              if (!this.isSelected(data) || this.isRowDisabled(data) || !data.newPrice || data.newPrice === data.price) {
                return '';
              }
              return controlValue > 0
                ? renderColoredArrow(RED_MARKER_COLOR)
                : renderColoredArrow(LIGHTGREEN_MARKER_COLOR, false);
            },
          },
        },
        {
          rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
          getRowId: (params) => String(getPbItemComponentId(params.data)),
          rowClassRules: {
            [ROW_DIRTY_CLASS]: ({ data }: { data: ItemsComponentsTable }) => data && this.isSelected(data),
          },
        });
  }

  getControlsValue(): ControlsValueDto {
    const itemMarkup = parseFloat(this.form.getControl('itemMarkup').value) || 0;
    const compMarkup =  parseFloat(this.form.getControl('compMarkup').value) || 0;
    const deletePct =  parseFloat(this.form.getControl('deletePct').value) || 0;
    return { itemMarkup, compMarkup, deletePct } ;
  }

  recalculate(): void {
    this.deselectRows();
    this.recalculateData();
    this.gridApi?.redrawRows();
  }

    // deselect rows with data < 0
  deselectRows(): void {
    if (!this.targetSelectionModel.isExcludeMode && this.targetSelectionModel.idList.length) {
      this.loadedData.forEach((item) => {
        if (this.isRowDisabled(item) && this.targetSelectionModel.idList.includes(getPbItemComponentId(item))) {
          this.targetSelectionModel.idList.splice(this.targetSelectionModel.idList.indexOf(getPbItemComponentId(item)), 1);
        }
      });
    } else if (this.targetSelectionModel.isExcludeMode) {
      this.loadedData.forEach((item) => {
        if (this.isRowDisabled(item) && !this.targetSelectionModel.idList.includes(getPbItemComponentId(item))) {
          this.targetSelectionModel.idList.push(getPbItemComponentId(item));
        }
      });
    }
    if (this.targetSelectionModel.isExcludeMode && this.targetSelectionModel.idList.length === this.loadedData.length) {
      this.targetSelectionModel.isExcludeMode = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  get completeFlow(): ActionFlow<FormFlowState<any>> {
    return this.pipelines.complete(
      {
        execute: this.commonActions.execute(() => {
          const items = this.targetSelectionModel.isExcludeMode ?
            this.loadedData.filter((item) => !this.targetSelectionModel.idList.includes(getPbItemComponentId(item))) :
            this.loadedData.filter((item) => this.targetSelectionModel.idList.includes(getPbItemComponentId(item)));
          const costCenterIds = [] as string[];
          items.forEach((item) => {
            if (!costCenterIds.includes(item.costCenterId)) {
              costCenterIds.push(item.costCenterId);
            }
          });
          const costCenters = costCenterIds.map((costCenterId) => ({
            ...this.priceBook.costCenters.find((item: PbCostCenterDto) => item.costCenterId === costCenterId),
             dirtyType: DirtyType.Updated,
          }));
          const detailsDto = {
            ...this.priceBook,
            costCenters,
            housePlans: [],
            items: items.map((item) =>
              omit({
                     ...item,
                     price: item.newPrice,
                     dirtyType: DirtyType.Updated,
                   },
                   'newPrice',
                   'trendPrice',
              )),
            isPersist: true,
          } as PbDetailsSave;
          return this.businessService.save(omit(detailsDto, 'housePlanIds', 'repriceDate'));
        }),
        finish: (fs: FormFlowState<any>) => {
          this.priceBook.version = fs.value.version;
          this.targetSelectionModel.isExcludeMode = false;
          this.targetSelectionModel.idList = [];
          this.gridApi?.deselectAll();
          this.loadData();
          },
        error: (error: any) => {
          this.notificationService.processAll(error.error);
          } ,
      });
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.targetSelectionModel = model;
    this.deselectRows();
    this.recalculateData();
    this.gridApi?.redrawRows();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  isSelected(data: PbCcElementDto): boolean {
    if (!data) {
      return  false;
    }
    if (!this.targetSelectionModel.isExcludeMode) {
      return this.targetSelectionModel.idList.includes(getPbItemComponentId(data));
    } else {
      return !this.targetSelectionModel.idList.includes(getPbItemComponentId(data));
    }
  }

  isRowDisabled(data: ItemsComponentsTable | any): boolean {
    if (!data || !this.form) {
      return true;
    }
    const { itemMarkup, compMarkup, deletePct } = this.getControlsValue();
    if (!itemMarkup && !compMarkup && !deletePct) {
      return true;
    }
    const id = data.id || getPbItemComponentId(data);
    const item = this.backupData.find((backupItem) => getPbItemComponentId(backupItem) === id);
    if (!item) {
      return true;
    }

    if (item.isComponent) {
      if (!compMarkup && !deletePct) {
        return true;
      }
      if (item.markupPct + compMarkup < 0) {
        return true;
      }  else if (deletePct && item.deletePct + deletePct < 0) {
        return  true;
      }
    } else {
      if (!itemMarkup && !deletePct) {
        return true;
      }
      if (item.markupPct + itemMarkup < 0) {
        return true;
      } else if (deletePct && item.deletePct + deletePct < 0) {
        return  true;
      }
    }
    return false;
  }

  recalculateData(): void {
    const { itemMarkup, compMarkup, deletePct } = this.getControlsValue();
    this.canBeSelectedCount = 0;
    this.loadedData.forEach((item) => {
      const backupItem = this.backupData.find((backupDataItem) => getPbItemComponentId(backupDataItem) === getPbItemComponentId(item)) || <any>{};
      const rowDisabled = this.isRowDisabled(item);
      if (!rowDisabled) {
        this.canBeSelectedCount++;
      }
      if (!this.isSelected(item) || rowDisabled) {
        for (const key in backupItem) {
          if (Object.prototype.hasOwnProperty.call(backupItem, key)) {
            item[key] = backupItem[key];
          }
        }
        delete item.newPrice;
        delete item.trendPrice;
      } else {
        if (item.isComponent) {
          item.markupPct =  compMarkup ? compMarkup + backupItem.markupPct : backupItem.markupPct;
          item.newPrice = compMarkup ? (item.cost * (1 + (backupItem.markupPct + compMarkup) / 100)) : '';
        } else {
          item.markupPct =  itemMarkup ? itemMarkup + backupItem.markupPct : backupItem.markupPct;
          item.newPrice = itemMarkup ? (item.cost * (1 + (backupItem.markupPct + itemMarkup) / 100)) : '';
        }
        if (item.newPrice) {
          item.trendPrice = item.newPrice > item.price ? 1 : item.newPrice < item.price ? -1 : 0;
        }
        item.deletePct = deletePct ? deletePct + backupItem.deletePct : backupItem.deletePct;
      }
    });
    if (this.canBeSelectedCount === 0) {
      this.targetSelectionModel.isExcludeMode = false;
      this.targetSelectionModel.idList = [];
    }
    this.changeDetectorRef.detectChanges();
  }

  onFocusOut(event: any, controlName: 'itemMarkup' | 'compMarkup' | 'deletePct'): void {
    if (event.target.value === '') {
      this.form.getControl(controlName).setValue(0);
    }
  }

  private loadData(): void {
    this.targetSelectionModel = emptySelectionModel();
    const request = {
      priceBookId:  this.priceBook.priceBookId,
      items: [],
      version:  this.priceBook.version,
    } as PbDetailsGetItemsComponentsRequest;
    if (this.form.getControl('costCenter').value === 'All') {
      this.businessService.loadItemsComponentsByPD(request).pipe(this.waitingService.operator())
        .subscribe((res) => {
          this.loadedData = res;
          this.backupData = res.map((item) => ({...item}));
          this.changeDetectorRef.detectChanges();
        },         this.notificationService.processAll);
    } else {
      this.businessService.loadCostCenter({
                                            priceBookId: this.priceBook.priceBookId || '',
                                            costCenterId: this.form.getControl('costCenter').value,
                                            items: [],
                                          })
        .pipe(this.waitingService.operator())
        .subscribe((res) => {
          this.loadedData = res;
          this.backupData = res.map((item) => ({...item}));
          this.changeDetectorRef.detectChanges();
      },           this.notificationService.processAll);
    }
  }
}
